import React, { useState } from 'react';
import './WhatsNew.css';
import { ReactComponent as CloseIcon } from '../../assets/icons/close-icon.svg';
import { ReactComponent as ArrowRightIcon } from '../../assets/icons/arrow-right-icon.svg';
import mcpImage from '../../assets/images/mcp_new.png';
import pnlImage from '../../assets/images/PNL_new.png';
import bsImage from '../../assets/images/BS_new.png';

// Bump this when the slides change so previously-dismissed users see the new
// release. The localStorage gate key is derived from this value.
export const WHATS_NEW_VERSION = '2026-05-05';
export const WHATS_NEW_STORAGE_KEY = `vairo_whats_new_seen_${WHATS_NEW_VERSION}`;

// ─── Slide config ────────────────────────────────────────────────────────────
// To swap in a real image later, import it at the top of this file and set the
// `image` field on the corresponding slide. Leave it `null` to render the
// rounded placeholder.
//   import mcpImage from '../../assets/images/whats-new/mcp.png';
//   { id: 'mcp', ..., image: mcpImage }
// ─────────────────────────────────────────────────────────────────────────────
const MCP_URL = 'https://api.vairo.ai/mcp';

const SLIDES = [
    {
        id: 'mcp',
        eyebrow: 'New integration',
        title: 'FP&A in Claude & ChatGPT',
        description:
            "Connect Vairo as an MCP server in Claude or ChatGPT and give your AI agents direct access to your live financial data model — query actuals, explore forecasts, and run analyses without leaving chat.",
        image: mcpImage,
        footnote: (
            <>
                Add this URL as a custom MCP server:{' '}
                <a
                    href={MCP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whats-new-link"
                >
                    {MCP_URL}
                </a>
            </>
        ),
    },
    {
        id: 'pnl',
        eyebrow: 'New report',
        title: 'Profit & Loss Statement',
        description:
            "A polished P&L view, fully driven by your connected data sources. Drill into revenue, COGS, and operating expenses across any period — no spreadsheets required.",
        image: pnlImage,
    },
    {
        id: 'bs',
        eyebrow: 'New report',
        title: 'Balance Sheet',
        description:
            "See your assets, liabilities, and equity in one place. Vairo's Balance Sheet stays in lockstep with your accounting system and your forecast.",
        image: bsImage,
    },
];

const WhatsNew = ({ onClose }) => {
    const [index, setIndex] = useState(0);
    const slide = SLIDES[index];
    const isLast = index === SLIDES.length - 1;
    const isFirst = index === 0;

    const handleNext = () => {
        if (isLast) {
            onClose();
            return;
        }
        setIndex(i => Math.min(SLIDES.length - 1, i + 1));
    };

    const handlePrev = () => {
        setIndex(i => Math.max(0, i - 1));
    };

    return (
        <div
            className="whats-new"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="What's new"
        >
            <div className="whats-new-header">
                <div className="whats-new-eyebrow">What's new this week</div>
                <button
                    type="button"
                    className="whats-new-close"
                    onClick={onClose}
                    aria-label="Close"
                >
                    <CloseIcon />
                </button>
            </div>

            <div className="whats-new-media">
                <button
                    type="button"
                    className="whats-new-nav prev"
                    onClick={handlePrev}
                    disabled={isFirst}
                    aria-label="Previous"
                >
                    <ArrowRightIcon />
                </button>

                <div className="whats-new-image">
                    {slide.image ? (
                        <img src={slide.image} alt={slide.title} />
                    ) : (
                        <div className="whats-new-image-placeholder" aria-hidden="true">
                            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="3" ry="3" />
                                <circle cx="8.5" cy="9.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" />
                            </svg>
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    className="whats-new-nav next"
                    onClick={handleNext}
                    aria-label={isLast ? 'Done' : 'Next'}
                >
                    <ArrowRightIcon />
                </button>
            </div>

            <div className="whats-new-body">
                <div className="whats-new-tag">{slide.eyebrow}</div>
                <div className="whats-new-title">{slide.title}</div>
                <div className="whats-new-description">{slide.description}</div>
                {slide.footnote && (
                    <div className="whats-new-footnote">{slide.footnote}</div>
                )}
            </div>

            <div className="whats-new-footer">
                <div className="whats-new-dots" role="tablist">
                    {SLIDES.map((s, i) => (
                        <button
                            key={s.id}
                            type="button"
                            className={`whats-new-dot ${i === index ? 'active' : ''}`}
                            onClick={() => setIndex(i)}
                            role="tab"
                            aria-selected={i === index}
                            aria-label={`Go to slide ${i + 1}: ${s.title}`}
                        />
                    ))}
                </div>
                <button
                    type="button"
                    className="whats-new-cta"
                    onClick={handleNext}
                >
                    {isLast ? 'Got it' : 'Next'}
                </button>
            </div>
        </div>
    );
};

export default WhatsNew;
